//
import { getDefaultState } from './state';
import * as getters from './getters';
import { defineStore } from 'pinia';
import bff from '@/shared/api/bff';
import { useGlobalStore } from '@/stores/global/global.store.ts';
import { usePolicyStore } from '../../policy/store/policy.store';

import {
  type IChildRiderAcknowledgement,
  type IChildRiderAgreementIDAndType,
  type IChildRiderEDelivery,
  type IChildRiderNewPackageInfo,
  type IChildRiderPackage,
  type IChildRiderPolicy,
  type IChildRiderPrimary,
  STEP,
} from './childriderStore.model';
import type { IDocumentMetadata } from '../../../shared/api/models.content';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import content from '@/shared/api/content';
import type { IGlobalStoreDocumentsItem } from '@/stores/global/globalStore.model.ts';
import type { AxiosResponse } from 'axios';

export const useChildRiderStore = defineStore('childrider', {
  state: getDefaultState,
  getters,
  actions: {
    setPackages(packages: IChildRiderPackage[]) {
      this.packages = packages.sort(
        (a, b) =>
          new Date(a.primary?.DOB ?? '').getTime() - new Date(b.primary?.DOB ?? '').getTime()
      );
    },
    setPackage(_package: IChildRiderPackage) {
      const foundIndex = this.packages.findIndex(
        (pkge) => pkge.policyId === _package.policyId && pkge.packageId === _package.packageId
      );
      if (foundIndex !== -1) {
        // this.packages[foundIndex] = { ...this.packages[foundIndex], ..._package },
        this.packages[foundIndex] = { ...this.packages[foundIndex], ..._package };
      } else {
        this.packages.push(_package);
      }
    },
    setLrcInd(_lrcInd: string) {
      this.lrcInd = _lrcInd;
    },
    setNewPolicy(newPolicy: IChildRiderPolicy) {
      this.newPolicy = newPolicy;
    },
    setNewPolicyEDelivery(eDelivery: IChildRiderEDelivery) {
      this.newPolicy.eDelivery = { ...this.newPolicy.eDelivery, ...eDelivery };
    },
    setNewPolicyAcknowledgement(acknowledgement: IChildRiderAcknowledgement) {
      this.newPolicy.acknowledgement = { ...this.newPolicy.acknowledgement, ...acknowledgement };
    },
    /**
     * set selected package index if package info is provided, clears package index otherwise
     */
    setSelectedPackageIndex(packageInfo: IChildRiderNewPackageInfo | null) {
      if (packageInfo) {
        const foundIndex = this.packages.findIndex(
          (pkge) =>
            pkge.policyId === packageInfo.policyId && pkge.packageId === packageInfo.packageId
        );
        this.newPackageInfo.packageIndex = foundIndex;
      } else {
        this.newPackageInfo.packageIndex = -1;
      }
    },
    /**
     * set new package info packageId, policyId, and primary if package info is provided, clears package index otherwise
     */
    setNewPackageInfo(packageInfo: IChildRiderNewPackageInfo | null) {
      if (packageInfo) {
        this.newPackageInfo.packageId = packageInfo.packageId;
        this.newPackageInfo.policyId = packageInfo.policyId;
        this.newPackageInfo.primary = packageInfo.primary;
      } else {
        this.newPackageInfo.packageId = '';
        this.newPackageInfo.policyId = '';
        this.newPackageInfo.primary = {
          firstName: '',
          lastName: '',
          middleName: '',
          DOB: '',
          gender: '',
          SSN: '',
        };
      }
    },
    /**
     * setSelectedOption - set the selected option from the user
     */
    setSelectedOption(selectedOption: string) {
      this.newPackageInfo.selectedOption = selectedOption;
    },
    /**
     * setOptIn - set the selected option from the user
     */
    setOPTIN(optIn: string, agreement: IChildRiderAgreementIDAndType) {
      this.newPackageInfo.optIn = optIn;
      if (optIn === 'Y') {
        this.newPackageInfo.eDelivery.agreementId = agreement.agreementId;
        this.newPackageInfo.eDelivery.agreementType = agreement.agreementType;
      }
    },
    /**
     * setPersonalInfo - set the new package info from the user
     */
    setPersonalInfo(personalInfo: IChildRiderPrimary) {
      this.newPackageInfo.primary = personalInfo;
    },
    setConversionProcessStep(step: number) {
      this.conversionProcess.step = step;
    },
    setEDelivery(eDeliveryData: IChildRiderEDelivery) {
      this.newPolicy.eDelivery = eDeliveryData;
    },
    async pullPackages() {
      const response = await bff.pullPackages();
      if (response.status === 200) {
        this.setPackages(response.data);
      } else {
        throw new Error('Pulling CRC packages failed.');
      }
    },
    async pullPackage() {
      const policyStore = usePolicyStore();
      const curentPolicyId = policyStore.currentPolicy.contractId;
      const policyPackage = this.packagesGivenPolicyId(curentPolicyId)[0];
      const response = await bff.pullPackage(curentPolicyId, policyPackage.packageId);
      if (response.status === 200) {
        this.setPackage(response.data);
        this.setLrcInd(response.data?.lrcInd);
      } else {
        throw new Error('Pulling CRC package failed.');
      }
    },
    async pullAgreement(type: string) {
      const policyStore = usePolicyStore();
      const policyNumber = policyStore.currentPolicy.contractId;
      const locale = useGlobalStore().getLocale;
      const res: AxiosResponse = await bff.getAgreement(type, locale, policyNumber);
      if (res.status === 200 && res.data.agreementId && type === 'D') {
        this.setEDelivery(res.data);
      } else if (res.status === 200 && res.data.agreementId && type === 'C') {
        this.setNewPolicyAcknowledgement(res.data);
      }
    },
    resetCRCData() {
      // clear data
      this.newPolicy = {
        packageIndex: -1,
        policyId: '', // parent policy id
        packageId: '',
        newPolicyId: '', // submitted policy id (temporary)
        firstName: '', // retrieved from life-policies-sys primInsFirstName; new policy - primary first name; goes to NB_COSMO_CRC
        lastName: '', // retrieved from life-policies-sys primInsLastName; new policy - primary middle name; goes to NB_COSMO_CRC
        middleName: '', // retrieved from life-policies-sys primInsMiddleName; new policy - primary last name; goes to NB_COSMO_CRC
        DOB: '', // retrieved from life-policies-sys primInsDob; new policy - primary dob; goes to NB_COSMO_CRC
        gender: '', // new policy - gender; goes to NB_COSMO_CRC
        SSN: '', // new policy - SSN; goes to NB_COSMO_CRC
        selectedOption: '',
        selectedFaceAmount: '',
        selectedPremiumAmount: '',
        eDelivery: {
          agreementId: -1,
          agreementLink: '',
          agreementType: 'D',
          createTs: '',
          optIn: '', // goes to NB_COSMO_CRC E_DELIVERY ('Y' or 'N')
        },
        acknowledgement: {
          agreementId: -1,
          agreementLink: '',
          agreementType: 'C',
          createTs: '',
          agreementTextId: 0,
          text: [],
        },
        requestNumber: '',
        paymentMode: '01',
        optIn: '',
        loading: false,
        primary: undefined,
      };
    },

    resetState() {
      Object.assign(getDefaultState());
    },

    async crcInit() {
      // pull list of package summaries from BFF
      await this.pullPackages();
      this.step = STEP.READY; // go to `READY` step
    },

    setStep(step: number) {
      this.step = step;
    },

    setExpirationDate(expirationDate: Date | string) {
      this.expirationDate = expirationDate;
    },

    async crcStart() {
      this.resetCRCData(); // Clear out data from previous
      await this.pullPackage(); // pull package details from BFF
      const policyStore = usePolicyStore(); // set new policy package info
      const curentPolicyId = policyStore.currentPolicy.contractId;
      const _package: IChildRiderPackage = this.packagesGivenPolicyId(curentPolicyId)[0];
      this.newPolicy = { ...this.newPolicy, ..._package };

      // set new policy personal info
      if (_package.primary?.DOB) {
        this.newPolicy.firstName = _package.primary.firstName;
        this.newPolicy.lastName = _package.primary.lastName;
        this.newPolicy.middleName = _package.primary.middleName;
        this.newPolicy.DOB = _package.primary.DOB;
        this.newPolicy.gender = _package.primary.gender;
      }
      await this.pullAgreement('D');

      // set acknowledgement
      await this.pullAgreement('C');

      // set Expiration Date
      this.setExpirationDate(this.expireDate);

      // go to `A_PERSONAL_INFO` step
      this.setStep(STEP.A_PERSONAL_INFO);
    },

    crcNext() {
      this.setStep(this.step + 1); // go to next step
    },

    timeout(ms: number) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async pushPackage() {
      const newPolicy = useChildRiderStore().newPolicy;
      const primary: IChildRiderPrimary = (({ firstName, middleName, lastName, gender, SSN }) => ({
        firstName,
        middleName,
        lastName,
        gender,
        SSN,
      }))(newPolicy);
      const eDelivery: IChildRiderEDelivery = newPolicy.eDelivery;
      const acknowledgement: IChildRiderAcknowledgement = newPolicy.acknowledgement;
      delete eDelivery.agreementLink;
      delete eDelivery.createTs;
      delete eDelivery.agreementVersion;
      delete acknowledgement.agreementTextId;
      delete acknowledgement.text;
      delete acknowledgement.agreementLink;
      delete acknowledgement.createTs;
      delete acknowledgement.agreementVersion;
      const request = {
        primary,
        eDelivery: {},
        acknowledgement: {},
        selectedOption: '',
        policyId: '',
        packageId: '',
        isNextgenPolicy: false,
      };
      request.eDelivery = eDelivery;
      request.acknowledgement = acknowledgement;
      request.selectedOption = newPolicy.selectedOption;
      request.policyId = newPolicy.policyId;
      request.packageId = newPolicy.packageId;
      request.isNextgenPolicy = this.isNextGen();
      const response = await bff.updatePackage(request);
      // throw error in case of non-200 response
      if (Math.floor(response.status / 100) !== 2) {
        throw new Error('Pushing CRC package update failed.');
      }

      const policy = response.data;
      this.setRequestNumber(policy.newPolicyNumber);
    },

    async crcSubmit() {
      await this.pushPackage(); // push package update (new policy from conversion)
      await this.timeout(10000); // wait for 10 seconds to allow the new policy to be created
      this.setStep(STEP.F_SUCCESS);
    },

    crcCancel() {
      // Clear out data from previous
      this.resetCRCData();
      // go back to `READY` step
      this.setStep(STEP.READY);
    },

    isNextGen: function () {
      /*
      Identifying NextGen CRC packets (see https://pri-jira.atlassian.net/browse/MYP-2896):
      Per LifeOps team (Chris Thompson), there will not be a NextGen indicator added to the current CRC service / api
      to denote which policies are NextGen versus pre-NextGen. Instead, NextGen CRC packets will not have tobacco rates.
      The following CRC_EVENT_LOG table data fields should contain zero (0 or 0.00) values:
      OPT1_T_PREM, OPT2_T_PREM, OPT3_T_PREM, OPT4_T_PREM, OPT5_T_PREM */
      const selectedPackage = this.NPPackage;

      const options = Object.entries(selectedPackage?.selectionOptions ?? []);
      const tobaccoRates: string | number[] = [];
      if (!options.length) {
        return false;
      }
      // filter out the tobacco rates
      options.forEach(([key, value]) => {
        if (key.includes('T')) {
          tobaccoRates.push(
            Number((value as unknown as { premiumAmount: string | number }).premiumAmount)
          );
        }
      });
      // check if packets does not have tobacco rates (premium amounts have 0 or 0.00 values)
      return tobaccoRates.every((e) => e <= 0);
    },

    // ***
    // calls content manager
    async openPDFPackage(objectCode: string) {
      const policyStore = usePolicyStore();
      const globalStore = useGlobalStore();
      const policyDocs = globalStore.getDocumentsOfPolicy(policyStore.currentPolicy?.contractId);
      const documentInfo = policyDocs.find(
        (document: IGlobalStoreDocumentsItem) =>
          document.documentType === 'S' &&
          (document.objectCode === objectCode || document.metadata?.objectCode === objectCode)
      );
      const date = documentInfo?.dateReceived;
      const formattedDate = date?.slice(0, date.indexOf('T'));
      const documentMetadata: IDocumentMetadata = {
        opComp: this.NPPackage?.policyCompNum ?? documentInfo?.metadata?.opComp ?? '',
        opArea: documentInfo?.operationalArea ?? documentInfo?.metadata?.opArea ?? '',
        contract: this.NPPackage?.policyNum ?? documentInfo?.metadata?.contract ?? '',
        objectCode: documentInfo?.objectCode ?? documentInfo?.metadata?.objectCode ?? '',
        dateReceived: formattedDate ?? documentInfo?.metadata?.dateReceived,
      } as {
        opComp: string;
        opArea: string;
        contract: string;
        objectCode: string;
        dateReceived: string;
      };
      const isWeb = CapacitorFacade.isWeb();
      let pdfURL;
      if (isWeb) {
        // download binary
        const pdfPackageBinary = await content.streamDocument(documentMetadata);

        //  create a url to show binary content
        pdfURL = URL.createObjectURL(new Blob([pdfPackageBinary], { type: 'application/pdf' }));
      } else {
        pdfURL = await content.streamDocument(documentMetadata);
      }
      return pdfURL;
    },

    setNPFirstName(firstName: string) {
      this.newPolicy.firstName = firstName;
    },

    setNPMiddleName(middleName: string) {
      this.newPolicy.middleName = middleName;
    },

    setNPLastName(lastName: string) {
      this.newPolicy.lastName = lastName;
    },

    setNPGender(gender: string) {
      this.newPolicy.gender = gender;
    },

    setNPDOB(DOB: string) {
      this.newPolicy.DOB = DOB;
    },

    setNPSSN(SSN: string) {
      this.newPolicy.SSN = SSN;
    },

    setNPSelectedOption(selectedOption: string) {
      this.newPolicy.selectedOption = selectedOption;
    },

    setNPSelectedFaceAmount(selectedFaceAmount: string) {
      this.newPolicy.selectedFaceAmount = selectedFaceAmount;
    },

    setNPSelectedPremiumAmount(selectedPremiumAmount: string) {
      this.newPolicy.selectedPremiumAmount = selectedPremiumAmount;
    },

    setNPEDeliveryOptIn(optIn: string) {
      this.newPolicy.eDelivery.optIn = optIn;
    },

    setLoading(loading: boolean) {
      this.newPolicy.loading = loading;
    },

    setRequestNumber(requestNumber: string) {
      this.newPolicy.requestNumber = requestNumber;
    },

    setAcknowledgement(acknowledgementData: IChildRiderAcknowledgement) {
      this.acknowledgement = acknowledgementData;
      this.newPackageInfo.acknowledgement.agreementId = acknowledgementData.agreementId;
      this.newPackageInfo.acknowledgement.agreementType = acknowledgementData.agreementType;
    },
  },
});
